import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';

/**
 * The ecosystem's ONE unsaved-changes confirmation.
 *
 * It is asked **in the surface's own footer — in place of its Cancel / Create
 * buttons — never in a second dialog stacked on top of it.** A modal over a
 * modal buries the very draft the question is about and gives the person two
 * overlays to unwind; the footer asks in place, with the form still visible.
 *
 * Three pieces:
 *  - {@link useDiscardGuardState} — the state machine a host surface owns
 *    (drawer / sheet / dialog). It turns every close path into `requestClose`:
 *    clean closes go straight through, dirty ones arm `confirming`.
 *  - {@link DiscardGuardProvider} — puts that state on context so a footer
 *    nested any number of components deep can swap itself without prop drilling.
 *  - {@link FooterDiscardGuard} — the swap itself: wrap a footer's contents and
 *    they are replaced by {@link DiscardBar} while the question is up.
 *
 * A surface that builds its own state (a plain Dialog, say) can still render
 * {@link DiscardBar} directly in its footer.
 */

export interface DiscardGuardState {
  /** Close the surface, routing through the guard when it is dirty. */
  requestClose: () => void;
  /** True while the footer must ask instead of showing its normal actions. */
  confirming: boolean;
  /** Answer: stay on the form. */
  keepEditing: () => void;
  /** Answer: throw the edits away and close. */
  discard: () => void;
  /** Question shown in the bar, e.g. "Discard changes?". */
  title: string;
  /** Muted follow-on line, e.g. "Your edits will be lost.". */
  description?: string;
  /** Label of the destructive button. */
  confirmLabel: string;
}

const DiscardGuardContext = React.createContext<DiscardGuardState | null>(null);

/** The nearest host surface's guard, or `null` when there is none. */
export function useDiscardGuard(): DiscardGuardState | null {
  return React.useContext(DiscardGuardContext);
}

export const DiscardGuardProvider = DiscardGuardContext.Provider;

export interface UseDiscardGuardOptions {
  /** Unsaved edits present — a close must ask first. */
  dirty?: boolean;
  /** Perform the actual close (the surface's `onOpenChange(false)` / `onClose`). */
  onClose: () => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
}

/**
 * Close-with-confirm state machine for a host surface. Wire `requestClose` into
 * every close path (Escape, outside-click, the header X, Cancel) and render the
 * footer through {@link FooterDiscardGuard}.
 */
export function useDiscardGuardState({
  dirty = false,
  onClose,
  title = 'Discard changes?',
  description = 'Your edits will be lost.',
  confirmLabel = 'Discard',
}: UseDiscardGuardOptions): DiscardGuardState & { reset: () => void } {
  const [confirming, setConfirming] = React.useState(false);

  const requestClose = React.useCallback(() => {
    if (dirty) setConfirming(true);
    else onClose();
  }, [dirty, onClose]);

  const keepEditing = React.useCallback(() => setConfirming(false), []);

  const discard = React.useCallback(() => {
    setConfirming(false);
    onClose();
  }, [onClose]);

  // Hosts call this when the surface re-opens, so a question left hanging by a
  // programmatic close (a successful save, a route change) never greets the next
  // open. Cheaper and lint-clean compared with an open-watching effect.
  const reset = React.useCallback(() => setConfirming(false), []);

  return React.useMemo(
    () => ({
      requestClose,
      confirming,
      keepEditing,
      discard,
      reset,
      title,
      description,
      confirmLabel,
    }),
    [requestClose, confirming, keepEditing, discard, reset, title, description, confirmLabel],
  );
}

export interface DiscardBarProps {
  title?: string;
  description?: string;
  confirmLabel?: string;
  keepLabel?: string;
  onKeepEditing: () => void;
  onDiscard: () => void;
  /** Match the footer's own button size. */
  size?: 'xs' | 'sm' | 'default';
  className?: string;
}

/**
 * The question itself: a warning line that takes the footer's free space, then
 * "Keep editing" and the destructive confirm where the primary action sat.
 * Renders as a fragment so it drops straight into any footer flex row.
 */
export function DiscardBar({
  title = 'Discard changes?',
  description,
  confirmLabel = 'Discard',
  keepLabel = 'Keep editing',
  onKeepEditing,
  onDiscard,
  size = 'sm',
  className,
}: DiscardBarProps) {
  return (
    <>
      <span
        // The footer swapping under them is a silent change for a screen-reader
        // user; announce the question the moment it replaces the actions.
        role="alert"
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 text-lg text-foreground',
          className,
        )}
      >
        <AlertTriangle
          className="h-4 w-4 shrink-0 text-destructive"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <span className="truncate">
          {title}
          {description ? (
            <> <span className="text-muted-foreground">{description}</span></>
          ) : null}
        </span>
      </span>
      <Button type="button" variant="outline" size={size} autoFocus onClick={onKeepEditing}>
        {keepLabel}
      </Button>
      <Button type="button" variant="destructive" size={size} onClick={onDiscard}>
        {confirmLabel}
      </Button>
    </>
  );
}

export interface FooterDiscardGuardProps {
  children: React.ReactNode;
  /** Match the footer's own button size. */
  size?: DiscardBarProps['size'];
  keepLabel?: string;
}

/**
 * Wrap a footer's contents: they render normally until the surface's guard is
 * asking, at which point the {@link DiscardBar} takes their place. No-op when no
 * guard is mounted, so a footer stays usable outside a guarded surface.
 */
export function FooterDiscardGuard({ children, size, keepLabel }: FooterDiscardGuardProps) {
  const guard = useDiscardGuard();
  if (!guard?.confirming) return <>{children}</>;
  return (
    <DiscardBar
      title={guard.title}
      description={guard.description}
      confirmLabel={guard.confirmLabel}
      keepLabel={keepLabel}
      size={size}
      onKeepEditing={guard.keepEditing}
      onDiscard={guard.discard}
    />
  );
}
